import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../shared/components/Button';
import Card from '../../../shared/components/Card';
import PhoneField from '../../../shared/components/PhoneField';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { loginWithPin } from '../utils/pinLogin';

const MemberSignIn = () => {
  const navigate = useNavigate();
  const [contactMethod, setContactMethod] = useState('whatsapp');
  const [contactValue, setContactValue] = useState('');
  const [phoneE164, setPhoneE164] = useState('');
  const [phoneMeta, setPhoneMeta] = useState({ isValid: false, isEmpty: true });
  const [pin, setPin] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const isPhoneMethod = contactMethod === 'whatsapp' || contactMethod === 'sms';

  // Reset inputs only when crossing the phone/email boundary (whatsapp<->sms keep state).
  const handleMethodChange = (value) => {
    const nextIsPhone = value === 'whatsapp' || value === 'sms';
    if (nextIsPhone !== isPhoneMethod) {
      setContactValue('');
      setPin('');
      setPhoneE164('');
      setPhoneMeta({ isValid: false, isEmpty: true });
    }
    setErrors({});
    setContactMethod(value);
  };

  const handlePhoneChange = ({ e164, isValid, isEmpty }) => {
    setPhoneE164(e164);
    setPhoneMeta({ isValid, isEmpty });
  };

  const validateForm = () => {
    const newErrors = {};

    if (isPhoneMethod) {
      if (phoneMeta.isEmpty) {
        newErrors.contact = 'Phone number is required';
      } else if (!phoneMeta.isValid) {
        newErrors.contact = 'Enter a valid number for the selected country';
      }
      if (!/^[0-9]{4}$/.test(pin)) {
        newErrors.pin = 'Enter your 4-digit PIN';
      }
    } else {
      if (!contactValue.trim()) {
        newErrors.contact = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactValue)) {
        newErrors.contact = 'Email is invalid';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // ── Email: unchanged OTP flow ──────────────────────────────────────────
      if (contactMethod === 'email') {
        const { error } = await supabaseClient.auth.signInWithOtp({
          email: contactValue,
          options: { shouldCreateUser: true, emailRedirectTo: undefined },
        });
        if (error) {
          setErrors({ general: error.message || 'Failed to send code. Please try again.' });
          return;
        }
        navigate('/verify', { state: { email: contactValue } });
        return;
      }

      // ── WhatsApp / SMS: phone + PIN via the pin-login Edge Function ─────────
      // phoneE164 is already normalized client-side (libphonenumber-js); the Edge
      // Function tolerates it and a session is minted only on a verified PIN.
      const result = await loginWithPin(phoneE164, pin);

      if (result.success) {
        // Mirror the post-email-OTP navigation exactly.
        const { data: { user } } = await supabaseClient.auth.getUser();
        localStorage.setItem('userRole', 'member');
        if (user) {
          localStorage.setItem('user', JSON.stringify({ id: user.id, email: user.email }));
        }
        navigate('/member/dashboard');
        return;
      }

      if (result.reason === 'locked') {
        const mins = result.locked_until
          ? Math.max(1, Math.ceil((new Date(result.locked_until) - new Date()) / 60000))
          : null;
        setErrors({
          general: mins
            ? `Too many attempts. Try again in about ${mins} minute${mins > 1 ? 's' : ''}.`
            : 'Too many attempts. Please try again later.',
        });
      } else {
        // invalid_credentials / anything else → generic, never reveal which was wrong.
        setErrors({ general: 'Incorrect phone number or PIN.' });
      }
    } catch (err) {
      setErrors({ general: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const getPlaceholder = () => {
    switch (contactMethod) {
      case 'whatsapp': return '+1234567890';
      case 'sms': return '+1234567890';
      case 'email': return 'member@example.com';
      default: return 'Enter contact info';
    }
  };

  const getIcon = () => {
    switch (contactMethod) {
      case 'whatsapp': return '💬';
      case 'sms': return '📱';
      case 'email': return '📧';
      default: return '📱';
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50">
      {/* Header */}
      <div className="text-center py-8">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2">Welcome back</h1>
        <p className="text-neutral-600">
          How would you like to sign in?
        </p>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <Card>
            <div className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Contact Method Selection */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-3">
                    Choose your sign-in method
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'whatsapp', label: 'WhatsApp number', icon: '💬' },
                      { value: 'sms', label: 'SMS/Phone number', icon: '📱' },
                      { value: 'email', label: 'Email address', icon: '📧' }
                    ].map((method) => (
                      <label key={method.value} className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-neutral-50">
                        <input
                          type="radio"
                          name="contactMethod"
                          value={method.value}
                          checked={contactMethod === method.value}
                          onChange={(e) => handleMethodChange(e.target.value)}
                          className="mr-3"
                        />
                        <span className="text-2xl mr-3">{method.icon}</span>
                        <span className="text-neutral-700">{method.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Contact Input */}
                <div>
                  <label htmlFor="contact" className="block text-sm font-medium text-neutral-700 mb-2">
                    {contactMethod === 'whatsapp' ? 'WhatsApp Number' : 
                     contactMethod === 'sms' ? 'Phone Number' : 'Email Address'} 
                    <span className="text-error-500">*</span>
                  </label>
                  {isPhoneMethod ? (
                    <PhoneField
                      id="contact"
                      defaultCountry="US"
                      onChange={handlePhoneChange}
                      error={!!errors.contact}
                      disabled={loading}
                      placeholder="Phone number"
                    />
                  ) : (
                    <input
                      id="contact"
                      type="email"
                      value={contactValue}
                      onChange={(e) => setContactValue(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-success-500 focus:border-transparent ${
                        errors.contact ? 'border-error-500' : 'border-neutral-300'
                      }`}
                      placeholder={getPlaceholder()}
                    />
                  )}
                  {errors.contact && (
                    <p className="mt-1 text-sm text-error-600">{errors.contact}</p>
                  )}
                </div>

                {/* PIN (phone methods only) */}
                {isPhoneMethod && (
                  <div>
                    <label htmlFor="pin" className="block text-sm font-medium text-neutral-700 mb-2">
                      4-digit PIN <span className="text-error-500">*</span>
                    </label>
                    <input
                      id="pin"
                      type="password"
                      inputMode="numeric"
                      maxLength={4}
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                      className={`w-full px-3 py-2 border rounded-lg text-center text-2xl tracking-[0.5em] focus:ring-2 focus:ring-success-500 focus:border-transparent ${
                        errors.pin ? 'border-error-500' : 'border-neutral-300'
                      }`}
                      placeholder="••••"
                      autoComplete="off"
                    />
                    {errors.pin && (
                      <p className="mt-1 text-sm text-error-600">{errors.pin}</p>
                    )}
                    <p className="mt-2 text-xs text-neutral-500">
                      Forgot PIN? Contact your group admin.
                    </p>
                  </div>
                )}

                {/* General Error */}
                {errors.general && (
                  <div className="bg-error-50 border border-error-200 rounded-lg p-3">
                    <p className="text-sm text-error-800">{errors.general}</p>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={loading}
                >
                  {loading
                    ? (isPhoneMethod ? 'Signing in...' : 'Sending...')
                    : (isPhoneMethod ? 'Sign in →' : 'Send Code →')}
                </Button>
              </form>
            </div>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-8">
        <p className="text-sm text-neutral-500">
          © 2026 EsuSol. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default MemberSignIn;
